import _, { compact, uniq, isEmpty, map, trim } from 'lodash';
import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Tooltip, Alert, Divider, Tag, InputNumber } from 'antd';
import { CircleCheckGreenIcon, InfoIcon } from "components/Icons";
import { FINDING_STATUS, SEVERITY_NAMES, FUTURE_RULES_ACCEPTED_TOOLS_LIST, SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES, SIMILAR_FINDINGS_TYPES, CAPITALIZED_SEVERITIES, TWISTLOCK_SOURCE, INFRA_SCAN_TYPES, FINDING_STATUS_DISPLAY_TEXT, FINDING_TICKET_STATUS, TOOL_FINDING_CATEGORIES, FINDING_STATUS_VOCABULARY_MAP, FUTURE_RULES_TOOLS_FIELDS_TO_CHECK, FUTURE_RULES_ACCEPTED_TOOLS_SCANTYPES, FINDING_PROPOSE_VOCABULARY_MAP, FINDING_VOCABULARY_TAKE_ACTION_MAP, FINDING_PROPOSED_STATUS } from './Constants'
import { KNOWN_TAG_TYPES, TAG_COLORS, TAG_PRIORITY_ORDER } from './FindingDetails/riskTagsConstants';
import Vocabulary, { getVocabulary } from 'components/Vocabulary';
import { editorRules, FieldTypes } from 'utils/formUtils';
import { CellRenderers } from 'utils/tableUtils';
import { updateURLSearch } from "utils/urlUtil";
import FindingAPI from 'restAPI/FindingAPI';
import { SEVERITY_COLOR } from 'constants/Layout';
import FilterableCell from 'components/FilterableCell';
import { ARMORCODE_GROUP_KEY } from 'utils/armorcodeGroupUtil';
import parseHtml from 'utils/parserUtil';
import InternalLink from 'components/InternalLink';
import ACLink from 'ac-components/ACLink/ACLink';
import { ASSESSMENT_TOOL_NAME } from 'views/Inventory/Assessments/Constants';
import ACCommentEditor from 'ac-components/ac-comment-editor/ACCommentEditor';

const { Link } = Typography;

function validateStatus(values, index, enabledFindingStatus, resultArr) {
    if (!_.isArray(values)) {
        values = [values];
    }
    if(values.length > 0) {
        const allMatch =  _.every(values, (value) => enabledFindingStatus.map(status => getVocabulary(FINDING_STATUS_VOCABULARY_MAP[status]).toLowerCase()).includes(value.toLowerCase()));
        if(!allMatch) {
            return resultArr.push(`Status is not appropriate at row ${index + 1}. Available status must be entered.`);                             
        }
    }
}

export const getFindingVocabulary = (fieldKey, key) => {
    if (fieldKey === "status" && FINDING_STATUS_VOCABULARY_MAP[key]) {
      return getVocabulary(FINDING_STATUS_VOCABULARY_MAP[key]);
    }
    if (fieldKey === "proposeAction") {
      if (FINDING_PROPOSE_VOCABULARY_MAP[key]) {
        if (key === FINDING_PROPOSED_STATUS.PROPOSE_DUE_DATE) {
            return `Propose ${getVocabulary("resolution")} Due Date`;
        }
        if (key === FINDING_PROPOSED_STATUS.PROPOSE_BASIC_CHANGE) {
            return FINDING_PROPOSE_VOCABULARY_MAP[key];
        }
        if (key === FINDING_PROPOSED_STATUS.PROPOSE_CUSTOM_FIELDS) {
            return FINDING_PROPOSE_VOCABULARY_MAP[key];
        }
        return `Propose ${getVocabulary(FINDING_PROPOSE_VOCABULARY_MAP[key])}`;
      };
      if (FINDING_VOCABULARY_TAKE_ACTION_MAP[key]) {
        if (key === FINDING_PROPOSED_STATUS.TAKE_ACTION_ON_PROPOSED_DUE_DATE) {
            return `Proposed ${getVocabulary("resolution")} Due Date`;
        }
        if (key === FINDING_PROPOSED_STATUS.TAKE_ACTION_ON_PROPOSED_ASSIGN_OWNER) {
            return `Proposed Assign ${getVocabulary('owner')}`;
        }
        if (key === FINDING_PROPOSED_STATUS.TAKE_ACTION_ON_PROPOSED_BASIC_CHANGE) {
            return FINDING_VOCABULARY_TAKE_ACTION_MAP[key];
        }
        return `Proposed ${getVocabulary(FINDING_VOCABULARY_TAKE_ACTION_MAP[key])}`;
      }
      if (FINDING_STATUS_VOCABULARY_MAP[key]) return getVocabulary(FINDING_STATUS_VOCABULARY_MAP[key]);
    }
    return key;
}

export const validateDraftFindingCSV = async (array)=> {
    if(_.isEmpty(array)){
        return ["Invalid csv file"];
      }
      const validateCWE = (value) => (!isNaN(value) && +value > 0) || _.isEmpty(value)
      const resultArr = [];
      const ruleObj = {
        "Severity":{
          severities: _.map(Object.values(SEVERITY_NAMES), severity => _.lowerCase(severity)),
        },
        "CVE":{
            rule: (values, index)=>{
                if (!_.isArray(values)) {
                    values = [values];
                }
                if(values.length > 0) {
                    const allMatch = _.every(values, (value) => {
                        if (_.isString(value)) {
                            const cveValues = value.split(',');
                            return _.every(cveValues, (cve) => {
                                const valueArr = cve.trim().split('-');
                                return valueArr.length === 3 && valueArr[0] === 'CVE' && valueArr[1].length === 4 && valueArr[2].length >= 4;
                            });
                        }
                    });
                    if(!allMatch) {
                        resultArr.push(`CVE is not appropriate at row ${index + 1}. CVE must be entered in the format: 'CVE-9999-9999'.`);
                    }
                }
            }
        },
        "CWE":{
            rule:(values, index)=>{
                if (!_.isArray(values)) {
                    values = [values];
                }
                if(values.length > 0) {
                    const allMatch = _.every(values, (value) => {
                        if (_.isString(value)) {
                            const cweValues = value.split(',');
                            return _.every(cweValues, (cwe) => {
                                const valueArr = cwe.trim().split('-');
                                return valueArr.length === 1 ? validateCWE(valueArr[0]) : (valueArr[0] === 'CWE' && validateCWE(valueArr[1]));
                            });
                        }
                    });
                    if(!allMatch) {
                        resultArr.push(`CWE is not appropriate at row ${index + 1}. CWE must be entered in the format 'CWE-XXX' or greater than or equal to 1.`)                                
                    }
                }
            }
        },
        "ToolFindingCategory":{
            toolCategory: _.map(TOOL_FINDING_CATEGORIES, toolCat => _.lowerCase(toolCat))
        },
        "Base Score":{
            rule:(value, index) => {
                if(value && isNaN(value)){
                        resultArr.push(`Base Score is not appropriate at row ${index + 1}. Only numbers are allowed.`) 
                }
            }
        },
        "Exploited":{
            rule:(value, index) => {
                if(value){
                    if(value.toLowerCase() !== "true" && value.toLowerCase() !== "false"){
                        resultArr.push(`Exploited is not appropriate at row ${index + 1}. Only true or false values are allowed.`)
                    }
                }
            }
        },
        "Description":{},
        "Steps To Reproduce":{},
        "Mitigation":{},
        "Impact":{},
      }
      const ruleKeys = Object.keys(ruleObj);

      const columns = Object.keys(array[0]) ;
      const missingColumn = [];
      for(const rule of ruleKeys){
          if(ruleObj[rule].require && columns.indexOf(rule) === -1){
             resultArr.push(`${rule} column is missing.`);
             missingColumn.push(rule)
          }
      }
      for(const [index, _field] of array.entries()){
          for(const key of ruleKeys){
             const fieldRule = ruleObj[key]
             if(fieldRule.require && missingColumn.indexOf(key) === -1 && _.isEmpty(_field[key])){
                 resultArr.push(`${key} is missing at row ${index + 1}.`)
             }
             if((key==="Description" || key==="Steps To Reproduce" || key === "Mitigation") && _field[key]){
                 await editorRules({ required: false, min: 0, maxByteSizeInKB: 100, maxMessage: true }).validator({}, _field[key]).catch(err=> resultArr.push(`${key} at row ${index + 1}: ${err}`))
             }
             if((key === "Impact") && _field[key]){
                 await editorRules({ required: false, min: 0, maxByteSizeInKB: 32, maxMessage: true }).validator({}, _field[key]).catch(err=> resultArr.push(`${key} at row ${index + 1}: ${err}`))
             }
             if(key === 'Severity' && _field[key] && fieldRule.severities.indexOf(_.lowerCase(_field[key])) === -1){
                 resultArr.push(`${key} type is not appropriate at row ${index + 1}. Appropriate severities are ${fieldRule.severities.join(', ')}.`)
             }
             if(key === 'CVE' && _field[key]){
               fieldRule.rule(_field[key], index)
             }
             if(key === 'CWE' && _field[key]){
               fieldRule.rule(_field[key], index)
             }
             if(key === 'ToolFindingCategory' && _field[key] && fieldRule.toolCategory.indexOf(_.lowerCase(_field[key])) === -1){
                resultArr.push(`${key} type is not appropriate at row ${index + 1}. Appropriate tool finding categories are ${fieldRule.toolCategory.join(', ')}.`)
              }
              if(key === "Base Score"){
                fieldRule.rule(_field[key], index)
              }
              if(key === "Exploited"){
                fieldRule.rule(_field[key], index)
              }
          }
      }
       return resultArr;
}

export const validateCSVFile = async (array, enabledFindingStatus, hasDisabledEnvironment, isMultiMode) => {
    if(_.isEmpty(array)){
      return ["Invalid csv file"];
    }

    const validateCWE = (value) => (!isNaN(value) && +value > 0) || _.isEmpty(value)
    const resultArr = [];
    const ruleObj = {
        "UniqueIdFromTool":{
            require: true
        },
        "Summary": {
            require: true
        },
        "Description": {
            require: true
        },
        "Mitigation": {
            require: true
        },
        "Group": isMultiMode ? {
            require: true,
        } : {},
        "Subgroup": isMultiMode ? {
            require: true,
        } : {},
        "Environment": (!isMultiMode || hasDisabledEnvironment) ? {} : {
            require: true
        },
        "Engagement": {},
        "Impact": {
            require: true
        },
        "Severity":{
          severities: _.map(Object.values(SEVERITY_NAMES), severity => getVocabulary(_.lowerCase(severity))),
          require: true
        },
        "ToolFindingCategory":{
            toolCategory: _.map(TOOL_FINDING_CATEGORIES, toolCat => _.lowerCase(toolCat))
        },
        "CVE":{
            rule: (values, index)=>{
                if (!_.isArray(values)) {
                    values = [values];
                }
                if(values.length > 0) {
                    const allMatch = _.every(values, (value) => {
                        if (_.isString(value)) {
                          const cveValues = value.split(',');
                          return _.every(cveValues, (cve) => {
                            const valueArr = cve.trim().split('-');
                            return valueArr.length === 3 && valueArr[0] === 'CVE' && valueArr[1].length === 4 && valueArr[2].length >= 4;
                        });
                        }
                    });
                    if(!allMatch) {
                        resultArr.push(`CVE is not appropriate at row ${index + 1}. CVE must be entered in the format: 'CVE-9999-9999'.`);
                    }
                }
            }
        },
        "CWE":{
            rule:(values, index)=>{
                if (!_.isArray(values)) {
                    values = [values];
                }
                if(values.length > 0) {
                    const allMatch = _.every(values, (value) => {
                        if(_.isString(value)){
                            const cweValues = value.split(',');
                            return _.every(cweValues, (cwe) => {
                                const valueArr = cwe.trim().split('-');
                                return valueArr.length === 1 ? validateCWE(valueArr[0]) : (valueArr[0] === 'CWE' && validateCWE(valueArr[1]));
                            })
                        }
                    });
                    if(!allMatch) {
                        resultArr.push(`CWE is not appropriate at row ${index + 1}. CWE must be entered in the format 'CWE-XXX' or greater than or equal to 1.`)                                
                    }
                }
            }
        },
        "Status": {
            rule: (values, index) => {
                return validateStatus(values, index, enabledFindingStatus, resultArr);
            }
        }    
    }
 const ruleKeys = Object.keys(ruleObj);

 const columns = Object.keys(array[0]) ;
 const missingColumn = [];
 for(const rule of ruleKeys){
     if(ruleObj[rule].require && columns.indexOf(rule) === -1){
        resultArr.push(`${rule} column is missing.`);
        missingColumn.push(rule)
     }
 }
 for(const [index, _field] of array.entries()){
     for(const key of ruleKeys){
        const fieldRule = ruleObj[key]
        if(fieldRule.require && missingColumn.indexOf(key) === -1 && _.isEmpty(_field[key])){
            resultArr.push(`${key} is missing at row ${index + 1}.`)
        }
        if((key==="Description" || key==="Steps To Reproduce" || key === "Mitigation") && _field[key]){
            await editorRules({ required: false, min: 0, maxByteSizeInKB: 100, maxMessage: true }).validator({}, _field[key]).catch(err=> resultArr.push(`${key} at row ${index + 1}: ${err}`))
        }
        if((key === "Impact") && _field[key]){
            await editorRules({ required: false, min: 0, maxByteSizeInKB: 32, maxMessage: true }).validator({}, _field[key]).catch(err=> resultArr.push(`${key} at row ${index + 1}: ${err}`))
        }
        if(key === 'Severity' && _field[key] && fieldRule.severities.indexOf(_field[key]) === -1){
            resultArr.push(`${getVocabulary(key)} type is not appropriate at row ${index + 1}. Appropriate severities are ${fieldRule.severities.join(', ')}.`)
        }
        if(key === 'ToolFindingCategory' && _field[key] && fieldRule.toolCategory.indexOf(_.lowerCase(_field[key])) === -1){
          resultArr.push(`${key} type is not appropriate at row ${index + 1}. Appropriate tool finding categories are ${fieldRule.toolCategory.join(', ')}.`)
        }
        if(key === 'CVE' && _field[key]){
          fieldRule.rule(_field[key], index)
        }
        if(key === 'CWE' && _field[key]){
          fieldRule.rule(_field[key], index)
        }
        if(key === 'Status' && _field[key]){
            fieldRule.rule(_field[key], index)
        }
     }
 }
  return resultArr;
}

export const validateUpdateFindingCSVFile = (array, enabledFindingStatus) => {
    if(_.isEmpty(array)){
      return ["Invalid csv file"];
    }
  
    const resultArr = [];
    const ruleObj = {
        "ID":{
            require: true
        },
        "Severity":{
          severities: _.map(Object.values(SEVERITY_NAMES), severity => getVocabulary(_.lowerCase(severity))),
        },
        "Status": {
            rule: (values, index) => {
                return validateStatus(values, index, enabledFindingStatus, resultArr);
            }
        }
    }
 const ruleKeys = Object.keys(ruleObj);

 const columns = Object.keys(array[0]) ;
 const missingColumn = [];
 for(const rule of ruleKeys){
     if(ruleObj[rule].require && columns.indexOf(rule) === -1){
        resultArr.push(`${rule} column is missing.`);
        missingColumn.push(rule)
     }
 }
 for(const [index, _field] of array.entries()){
     for(const key of ruleKeys){
        const fieldRule = ruleObj[key]
        if(fieldRule.require && missingColumn.indexOf(key) === -1 && _.isEmpty(_field[key])){
            resultArr.push(`${key} is missing at row ${index + 1}.`)
        }
        if(key === 'Severity' && _field[key] && fieldRule.severities.indexOf(_field[key]) === -1){
            resultArr.push(`${getVocabulary(key)} type is not appropriate at row ${index + 1}. Appropriate severities are ${fieldRule.severities.join(', ')}.`)
        }
        if(key === 'Status' && _field[key]){
            fieldRule.rule(_field[key], index)
        }
     }
 }
  return resultArr;
}

export const checkForAdditionalColumns = (array, supportedColumns) => {
    if(_.isEmpty(array)){
      return ["Invalid csv file"];
    }
    const columns = Object.keys(array[0]) ;
    return _.filter(columns, entry => supportedColumns.indexOf(entry) === -1);
}

export function getFindingStatusApiType(status) {
    switch (status) {
        case FINDING_STATUS.ACCEPT_RISK:
            return 'accept-risk';
        case FINDING_STATUS.CONFIRMED:
            return 'confirm';
        case FINDING_STATUS.OPEN:
            return 'open';
        case FINDING_STATUS.FALSE_POSITIVE:
            return 'false-positive';
        case FINDING_STATUS.SUPPRESSED:
            return 'suppressed';
        case FINDING_STATUS.TRIAGE:
            return 'triage';
        case FINDING_STATUS.IN_PROGRESS:
            return 'inProgress';
        case FINDING_STATUS.CONTROLLED:
            return 'controlled';
        default:
            return;
    }
};

export const useFindingScopeFields = ({
    finding,
    helpText,
    permission,
    scopeOnChange,
    similarFindings,
    redirectToBucket,
    isFieldMandatory = true,
    showFindingsTable = false,
    scope = 'FINDING',
    updateType,
    setUpdateType,
    setTagCondition }) => {

    const [fields, setFields] = useState([]);
    const [toolMatch, scanTypes] = useMemo(() => {
        const tool = _.find(FUTURE_RULES_ACCEPTED_TOOLS_LIST, (toolName) => _.startsWith(toolName, _.toLower(finding.toolName || finding.source))) || '';
        return [!!tool, FUTURE_RULES_ACCEPTED_TOOLS_SCANTYPES[tool] || []]
    }, [finding]);

    useEffect(() => {
        const data = [];
        let hasEmptyFields = false;
        if (_.has(FUTURE_RULES_TOOLS_FIELDS_TO_CHECK, _.toLower(finding.toolName))) {
            _.forEach(FUTURE_RULES_TOOLS_FIELDS_TO_CHECK[_.toLower(finding.toolName)], (field) => {
                if (_.isEmpty(finding[field])) {
                    hasEmptyFields = true;
                }
            })
        }
        if (toolMatch && !hasEmptyFields && (_.isEmpty(scanTypes) || _.includes(scanTypes, finding.scanType))) {
            const _scope = {
                name: 'scope',
                label: 'Scope',
                type: FieldTypes.SELECT,
                disabled: !permission,
                help: <>{helpText} <Typography.Link href={`https://app.armorcode.com/api/jwt/zendesk/sso?return_to=https://support.armorcode.com/hc/en-us/articles/30035296343571-Similar-Findings#h_01H9HZJCK1KK9Z97ET8T427DSZ`} target="_blank">Know more about similar findings</Typography.Link></>,
                rules: [{
                    required: isFieldMandatory
                }],
                options: [
                    { name: "For this finding", id: 'FINDING' },
                    { name: <>All similar findings for this <Vocabulary name="subProduct"/></>, id: 'SUBPRODUCT' },
                    { name: <>All similar findings for this <Vocabulary name="product"/></>, id: 'PRODUCT'},
                    { name: <>All similar findings for Organization</> , id: 'BUSINESS_UNIT' },
                ],
                props: {
                    onChange: scopeOnChange,
                },
                className: 'scope-extra-field',
                extra: similarFindings > 0 ? <Alert className='m-t-xs' message={<span><strong>{similarFindings} finding(s)</strong> will be updated.{showFindingsTable ? null : <><Link strong style={{textDecoration: "underline", color: "inherit"}}><span style={{ margin: '0px 3px 0 3px' }} onClick={redirectToBucket}> Click here </span></Link>to see complete list.</>}</span>} type="success" /> : ""
            }

            data.push(_scope);

            if (scope !== 'FINDING') {
                const tagLength = finding.tags?.length;
                const type = {
                    name: 'updateType',
                    label: 'Update Type',
                    type: FieldTypes.RADIO,
                    disabled: !permission,
                    options: [
                        {label: "Rule Based", value: SIMILAR_FINDINGS_TYPES.RULE_BASED},
                        {label: <Tooltip title={!tagLength && "No tags available"}>Tag Based</Tooltip>, value: SIMILAR_FINDINGS_TYPES.TAG_BASED, disabled: !tagLength }
                    ],
                    props: {
                        onChange: (e) => setUpdateType(e.target.value),
                        defaultValue: SIMILAR_FINDINGS_TYPES.RULE_BASED
                    }
                }
                data.push(type);
                if (updateType !== SIMILAR_FINDINGS_TYPES.RULE_BASED) {
                    const type = {
                        name: 'tagCondition',
                        label: 'Tag Condition',
                        type: FieldTypes.SELECT,
                        options: [
                            { label: "Has Any", value: SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.contains },
                            { label: "Has All", value: SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.equals }
                        ],
                        props: {
                            onChange: setTagCondition,
                            defaultValue: SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.equals
                        }
                    }
                    data.push(type);
                }
            }
        }
        setFields(data)
    }, [toolMatch, scanTypes, helpText, finding, permission, scopeOnChange, redirectToBucket, isFieldMandatory, showFindingsTable, scope, updateType, setUpdateType, setTagCondition, similarFindings])

    return fields
}

export const isCustomFinding = (testType) => _.startsWith(_.toLower(testType), 'custom') || testType === ASSESSMENT_TOOL_NAME;

export const canEditCustomField = (customField) => ["text", "text-long", "boolean", "long"].includes(customField.type?.toLowerCase());

export const sortStatsBasedOnSeverities = (data, severities = CAPITALIZED_SEVERITIES) => {
    const finalStats = [];
    severities.forEach(severity => {
        const severityStat = _.sortBy(data.filter(entry => !_.isUndefined(entry[severity]) && entry[severity] !== 0 ), [severity]).reverse()
        finalStats.push(...severityStat)
    })
    return _.uniq(finalStats)
}


export const getDivider = (index, arr = []) => {
    return index !== arr.length - 1 ? <Divider className="m-y-xs" /> : null;
}

export const renderMultipleTicketEntriesAsRows = (source = [], type = "armorcodeTicketId", showFilter= true) => {
    if (_.isEmpty(source)) {
        return CellRenderers.VALUE_OR_NA();
    }
    return (
        <>
            {
                source.map((entry, index) => {
                    const { isArmorcodeTicketClosed, armorcodeTicketStatus, armorcodeTicketUrl } = entry;

                    const value = entry[type];

                    return (
                        <div key={index} title={armorcodeTicketStatus}>
                            {
                                type === "armorcodeTicketId"
                                    ?   
                                    <FilterableCell showFilter={showFilter} filterKey='armorcodeTicket' value={value}>
                                            <Link href={armorcodeTicketUrl} target="_blank">
                                                <Tag title={armorcodeTicketStatus} className="text-ellipsis tools" style={{ marginRight: 2, marginBottom: -2, maxWidth: "100%", display: "inline-block" }} >
                                                    {isArmorcodeTicketClosed ? <CircleCheckGreenIcon className="m-r-xs" /> : null}<b>{value}</b>
                                                </Tag>
                                            </Link>
                                        </FilterableCell>
                                    : CellRenderers.VALUE_OR_NA(value)
                            }
                            {getDivider(index, source)}
                        </div>
                    )
                })
            }
        </>
    )
}

export function getFilterValues(props = {}){
    // removing {ignoreMitigated:false} until requirement is clear
    const fields = [
      "armorcodeTicketStatus",
      "owner",
      "businessOwner",
      "securityOwner",
      "engineeringOwner",
      "supportOwner",
      "complianceOwner",
      "team",
      "armorcodeProjects",
      "assetType",
      "assetOS",
      "assetOSVersions",
      "assetCloudProviders",
      "assetLocations",
      "assetOwner",
      "assessments",
      "owaspTop10_2021",
      "cweTop25_2024",
      "riskRegister"
    ]
    const data = {
      fields: props.fields || fields,
      businessUnits: props.businessUnits
    };

    const { promise: promise1, cancel: cancel1 } = FindingAPI.getFilterValues(data);

    const promise = promise1.then((filters) => {
      // All severities should be visible on filter since they are constant
      filters.severity = Object.keys(SEVERITY_COLOR).map(_.capitalize);
      filters.toolSeverity = filters.severity.slice();

      // Appending `S-` to scan so that it can be searched on
      // filters.scan = filters.scan.map(scanId => ({ name: `S-${scanId}`, id: scanId }));
      filters.toolProjectStatus = ['active', 'inactive'];
      filters.exploited = [{ name: 'True', id: true }];
      // Setting filter for twistlock-source directly for ENG-16232
      filters.Twistlock_scanType = [...TWISTLOCK_SOURCE];
      return filters;
    }, e => console.error(e));

    return {
        promise,
        cancel: cancel1
    };
  }

export const CurrentUserFilterOption = {name: "Current User", id: "-1"};
  
export const getAppSecAndInfraData = (data = []) => {
    const appSec = [], infra = [];
    data.forEach(d => {
        // if (d.configurationStatus === "ACTIVE") {
        const toolType = (d.toolType || '').toLowerCase();
        const hasInfra = INFRA_SCAN_TYPES.some(sType => toolType.includes(_.lowerCase(sType)));
        const tools = _.isEmpty(d.toolType) ? null : _.split(d.toolType, '/')

        const lowerCaseInfra = _.map(INFRA_SCAN_TYPES, _.lowerCase)
        const onlyInfra = _.filter(tools, (tool) => lowerCaseInfra.indexOf(_.lowerCase(tool)) === -1).length === 0;

        if (toolType.includes("/") && hasInfra && !onlyInfra) {
            appSec.push({ ...d, toolType: tools });
            infra.push({ ...d, toolType: tools });
        } else if (hasInfra) {
            infra.push({ ...d, toolType: tools });
        } else {
            appSec.push({ ...d, toolType: tools });
        }
        // }
    });
    return {
        appSec,
        infra
    }
}

export const DATE_SUPPORTED_FILTER_KEYS = ['slaDueDate', "triageDueDate",  "remediationDueDate",  'lastModifiedDate', 'foundOn', 'lastUpdated', 'issueResolved', 'lastConfirmDate', 'lastFalsePositiveDate', 'lastSeenDate'];

export const showResurfaceTimeForAcceptRisk = (record) => {
    if (!FINDING_STATUS_DISPLAY_TEXT[record.status]) {
        return "N/A";
    }
    return (record.status === FINDING_STATUS.ACCEPT_RISK ||
        record.status === FINDING_STATUS.CONTROLLED) &&
        record.unsupresTime ? (
        <>
            {FINDING_STATUS_DISPLAY_TEXT[record.status]}{" "}
            <Tooltip
            title={<>Resurface on {CellRenderers.DATE(record.unsupresTime)}</>}
            >
            <InfoIcon />
            </Tooltip>
        </>
    ) : (
        FINDING_STATUS_DISPLAY_TEXT[record.status]
    );
}

export const getTicketStatus = (record) => {
    let errorMsg = "";
    const ticketStatus = record?.status || record?.latestStatus;
    const ticketID = record.ticketDetailJpaDto?.ticketId || "";
    if (isTicketCreationFailed(ticketStatus)) {
        errorMsg = record?.errorMessage;
        return <Tooltip title={errorMsg}>Ticket Creation Failed</Tooltip>;
    } else if (
        ticketStatus === FINDING_TICKET_STATUS.CREATED ||
        ticketStatus === FINDING_TICKET_STATUS.DONE
    ) {
        return record.ticketDetailJpaDto?.url ? (
            <Typography.Text
                title={ticketID}
                className='text-ellipsis'
            >
                {ticketID}
            </Typography.Text>
        ) : (
            CellRenderers.VALUE_OR_NA(ticketID)
        );
    } else if (ticketStatus === FINDING_TICKET_STATUS.UPDATE_FAILED) {
        return (
            <>
                <Typography.Text
                    title={ticketID}
                    className='text-ellipsis'
                >
                    {ticketID}
                </Typography.Text>{" "}
                <Tooltip title={record?.errorMessage}>
                    <InfoIcon />
                </Tooltip>
            </>
        );
    } else {
        return _.isString(record.armorcodeTicketId) ? (
            <Typography.Text
                title={record.armorcodeTicketId}
                className='text-ellipsis'
            >
                {record.armorcodeTicketId}
            </Typography.Text>
        ) : (
            "Ticket Creation in Progress"
        );
    }
};

export const isTicketCreationFailed = (status) => {
    switch (status) {
        case FINDING_TICKET_STATUS.ERROR_OCCURRED:
        case FINDING_TICKET_STATUS.CREATE_FAILED:
            return true;
        default:
            break;
    }
    return false;
};


export const similarFindingsCount = (similarFindings, history) => {
    if (_.isNil(similarFindings)) {
        return CellRenderers.VALUE_OR_NA(similarFindings);
    }
    const updateFindings = (id) => {
        const filters = {id}
        updateURLSearch(history, { filters }, "/findings");
    }
    return (
        <Typography.Text
            className="clickable p-l-sm p-r-sm text-bold"
            onClick={() => updateFindings(similarFindings)}
            style={{ textDecoration: "underline", color: "#35198E" }}
        >
            {similarFindings.length}
        </Typography.Text>
    );
}

export const convertAggField = (key) => {
    if(key.startsWith("keyValue.")){
        return "tags";
    }
    return key === ARMORCODE_GROUP_KEY
      ? "tags"
      : key === "team"
      ? "teamId"
      : key;
}

export const getIncludeKeysField = (keys) => {
    return compact(
        map(uniq(keys), (key) => {
            if (key === ARMORCODE_GROUP_KEY) {
                return ARMORCODE_GROUP_KEY;
            }
            if(key.startsWith("keyValue.")){
                return key.replace("keyValue.", "");
            }
            return "";
        }),
    );
};

export const isTagFilterApplied = (filterInfo) => {
    const aql = (filterInfo.aqlQueryV2?.[0] ?? filterInfo.aqlQuery?.[0])?.toString() || "";
    return !_.isEmpty(filterInfo.keyValue) || aql.indexOf('tags') !== -1;
}


export const disableSelectAllFindings = (filterInfo) => {
  const aqlValue = filterInfo.aqlQueryV2 ?? filterInfo.aqlQuery;
  return aqlValue
    ? trim(aqlValue?.[0]).length === 0
    : isEmpty(filterInfo);
};

export const getCustomColumnValues = (tags, columnKey, ignoreCase = false) => {
  const values = [];
  const normalizedColumnKey = ignoreCase ? columnKey.toLowerCase() : columnKey;
  _.forEach(tags, (entry) => {
    const [key, ...remainingValues] = entry.split(":");
    const value = remainingValues.join(":");
    const normalizedKey = ignoreCase ? key.toLowerCase() : key;
    if (normalizedKey === normalizedColumnKey && !isEmpty(value)) {
      values.push(value);
    }
  });
  return values;
};

export const extractTagValue = (tag) => {
    const [, ...value] = tag.split(":");
    return value.join(":");
}

export const getImageName = (finding) => {
    const { containerImageName, containerImageTag } =
        finding.additionalDetails || {};
    return containerImageName
        ? containerImageTag
            ? `${containerImageName}:${containerImageTag}`
            : containerImageName
        : undefined;
  };
  
export const renderLastResolutionNote = (lastResolutionNote) => {
   if(lastResolutionNote) {
        const transform = (reactNode, node) => {
             if (node.type === 'tag' && node.name === 'a') {
                return (
                <InternalLink
                    to={node.attribs.href.replace('/#/', '/')}
                    component={(props) => <ACLink {...props}/>}
                    target='_blank'
                >
                    {/* We are expecting a simple link so the children's first element would contain the link text string */}
                    {node.children[0].data}
                </InternalLink>
                );
            }
            return reactNode;
        };

        return parseHtml(lastResolutionNote, { transform })
    }
    return CellRenderers.VALUE_OR_NA();
}

export const getFilteredTagKeys = (filters) => {
  // First, try to get from basic filter keyValue
  const basicFilterKeys = compact(filters?.keyValue?.map((keyValue) => keyValue.key) ?? []);

  if (basicFilterKeys.length > 0) {
    return basicFilterKeys;
  }

  // If no basic filter keys, try to extract from AQL query
  const aqlQuery = filters?.aqlQueryV2?.[0] ?? filters?.aqlQuery?.[0];
  if (!aqlQuery || typeof aqlQuery !== 'string') {
    return [];
  }

  return extractTagKeysFromAQL(aqlQuery);
};

/**
 * Extracts tag keys from an AQL query string
 *
 * Supported patterns:
 * - tags = 'key:value' or tags = "key:value" or tags = key:value (unquoted)
 * - tags = '"key.with.dots:value"' (inner quotes)
 * - tags = "\"key.with.dots:value\"" (escaped quotes)
 * - tags IN ('key1:value1', 'key2:value2') or tags IN (key1:value1, key2:value2)
 * - tags IN ('"key.first:value"', '"key.second:value"') (inner/escaped quotes)
 * - tags CONTAINS 'key' or tags CONTAINS key
 * - tags LIKE 'value%' or tags LIKE value%
 *
 * @param aql - AQL query string
 * @returns Array of tag keys (with inner quotes preserved)
 */
export const extractTagKeysFromAQL = (aql) => {
  if (!aql || typeof aql !== 'string') {
    return [];
  }

  const tagKeys = [];

  /**
   * Helper to unescape backslash-escaped quotes from a tag value
   * Handles: \"key:value\" becomes "key:value", preserving inner quotes
   */
  const unescapeQuotes = (value) => {
    if (!value) return value;
    // Unescape backslash-escaped quotes (\" becomes ", \' becomes ')
    return value.replace(/\\(["'])/g, '$1');
  };

  // Pattern 1: tags = 'value' or tags = "value" or tags = value (unquoted)
  // Use separate patterns for single and double quoted to handle escaped chars
  const singleTagSingleQuotedPattern = /\btags\s*=\s*'((?:[^'\\]|\\.)*)'/gi;
  const singleTagDoubleQuotedPattern = /\btags\s*=\s*"((?:[^"\\]|\\.)*)"/gi;
  let match;
  while ((match = singleTagSingleQuotedPattern.exec(aql)) !== null) {
    const tagValue = unescapeQuotes(match[1]);
    const key = tagValue.split(':')[0];
    if (key) {
      tagKeys.push(key);
    }
  }
  while ((match = singleTagDoubleQuotedPattern.exec(aql)) !== null) {
    const tagValue = unescapeQuotes(match[1]);
    const key = tagValue.split(':')[0];
    if (key) {
      tagKeys.push(key);
    }
  }
  // Unquoted values - match until space, ), or end of string
  const singleTagUnquotedPattern = /\btags\s*=\s*(?!['"])([^\s)]+)/gi;
  while ((match = singleTagUnquotedPattern.exec(aql)) !== null) {
    const tagValue = match[1];
    const key = tagValue.split(':')[0];
    if (key) {
      tagKeys.push(key);
    }
  }

  // Pattern 2: tags IN ('value1', 'value2', ...) - supports both quoted and unquoted
  const inPattern = /\btags\s+IN\s*\(([^)]+)\)/gi;
  while ((match = inPattern.exec(aql)) !== null) {
    const valuesStr = match[1];
    // Extract single-quoted values - handle escaped chars
    const singleQuotedPattern = /'((?:[^'\\]|\\.)*)'/g;
    let valueMatch;
    while ((valueMatch = singleQuotedPattern.exec(valuesStr)) !== null) {
      const tagValue = unescapeQuotes(valueMatch[1]);
      const key = tagValue.split(':')[0];
      if (key) {
        tagKeys.push(key);
      }
    }
    // Extract double-quoted values - handle escaped chars
    const doubleQuotedPattern = /"((?:[^"\\]|\\.)*)"/g;
    while ((valueMatch = doubleQuotedPattern.exec(valuesStr)) !== null) {
      const tagValue = unescapeQuotes(valueMatch[1]);
      const key = tagValue.split(':')[0];
      if (key) {
        tagKeys.push(key);
      }
    }
    // Extract unquoted values (split by comma, trim, filter out quoted ones)
    const values = valuesStr.split(',').map((v) => v.trim());
    values.forEach((v) => {
      // Skip if it's a quoted value (already processed above)
      if (!v.startsWith("'") && !v.startsWith('"') && v.length > 0) {
        const key = v.split(':')[0];
        if (key) {
          tagKeys.push(key);
        }
      }
    });
  }

  // Pattern 3: tags CONTAINS 'value' or tags CONTAINS value (unquoted)
  // Use separate patterns for single and double quoted to handle escaped chars
  const containsSingleQuotedPattern = /\btags\s+CONTAINS\s*'((?:[^'\\]|\\.)*)'/gi;
  const containsDoubleQuotedPattern = /\btags\s+CONTAINS\s*"((?:[^"\\]|\\.)*)"/gi;
  while ((match = containsSingleQuotedPattern.exec(aql)) !== null) {
    const tagValue = unescapeQuotes(match[1]);
    const key = tagValue.split(':')[0];
    if (key) {
      tagKeys.push(key);
    }
  }
  while ((match = containsDoubleQuotedPattern.exec(aql)) !== null) {
    const tagValue = unescapeQuotes(match[1]);
    const key = tagValue.split(':')[0];
    if (key) {
      tagKeys.push(key);
    }
  }
  // Unquoted values
  const containsUnquotedPattern = /\btags\s+CONTAINS\s*(?!['"])([^\s)]+)/gi;
  while ((match = containsUnquotedPattern.exec(aql)) !== null) {
    const tagValue = match[1];
    const key = tagValue.split(':')[0];
    if (key) {
      tagKeys.push(key);
    }
  }

  // Pattern 4: tags LIKE 'value%' or tags LIKE value% (unquoted)
  // Use separate patterns for single and double quoted to handle escaped chars
  const likeSingleQuotedPattern = /\btags\s+LIKE\s*'((?:[^'\\]|\\.)*)'/gi;
  const likeDoubleQuotedPattern = /\btags\s+LIKE\s*"((?:[^"\\]|\\.)*)"/gi;
  while ((match = likeSingleQuotedPattern.exec(aql)) !== null) {
    const tagValue = unescapeQuotes(match[1]).replace(/%/g, '');
    const key = tagValue.split(':')[0];
    if (key) {
      tagKeys.push(key);
    }
  }
  while ((match = likeDoubleQuotedPattern.exec(aql)) !== null) {
    const tagValue = unescapeQuotes(match[1]).replace(/%/g, '');
    const key = tagValue.split(':')[0];
    if (key) {
      tagKeys.push(key);
    }
  }
  // Unquoted values
  const likeUnquotedPattern = /\btags\s+LIKE\s*(?!['"])([^\s)]+)/gi;
  while ((match = likeUnquotedPattern.exec(aql)) !== null) {
    const tagValue = match[1].replace(/%/g, '');
    const key = tagValue.split(':')[0];
    if (key) {
      tagKeys.push(key);
    }
  }

  return uniq(compact(tagKeys));
};

/**
 * Parses a tag string in format "key:value" or "key=value"
 */
export function parseTag(tagString) {
  const separator = tagString.includes(":") ? ":" : "=";
  const [rawKey, rawValue] = tagString.split(separator).map((s) => s.trim());

  if (!rawKey || !rawValue) {
    return {
      key: tagString,
      value: "",
      displayKey: tagString,
      displayValue: "",
      isKnown: false,
      color: TAG_COLORS.NEUTRAL,
    };
  }

  const normalizedKey = rawKey.toLowerCase();
  const config = KNOWN_TAG_TYPES[normalizedKey];

  return config
    ? {
        key: normalizedKey,
        value: rawValue,
        displayKey: rawKey,
        displayValue: rawValue,
        isKnown: true,
        config,
        color: config.getColor(rawValue),
      }
    : {
        key: rawKey,
        value: rawValue,
        displayKey: rawKey,
        displayValue: rawValue,
        isKnown: false,
        color: TAG_COLORS.NEUTRAL,
      };
}

/**
 * Get sort priority for a tag key (lower = higher priority)
 * Unknown tags get priority after all known tags
 */
const getTagPriority = (key) => {
  const normalizedKey = key.toLowerCase();
  const index = TAG_PRIORITY_ORDER.indexOf(normalizedKey);
  return index === -1 ? TAG_PRIORITY_ORDER.length : index;
};

/**
 * Sort parsed tags by priority, then alphabetically for "Other" tags
 */
const sortParsedTags = (tags) => {
  return [...tags].sort((a, b) => {
    const priorityA = getTagPriority(a.key);
    const priorityB = getTagPriority(b.key);

    // First compare by priority
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // If same priority (both "Other"), sort alphabetically by key
    return a.key.localeCompare(b.key);
  });
};

/**
 * Parses an array of tag strings and returns them sorted by priority
 */
export const parseTags = (tags) => {
  if (!Array.isArray(tags) || tags.length === 0) {
    return [];
  }
  const parsed = tags.map(parseTag);
  return sortParsedTags(parsed);
};


export function getCustomFieldProps(field) {
  switch(field.type) {
    case 'boolean':
      return {
        type: FieldTypes.SWITCH,  
      };
    case 'long':
      return {
        render: () => (
            <InputNumber precision={0} min={0} step={1} style={{ width: "100%" }} placeholder="Enter Whole number" />
        ),
      }
    default:
      return {
        render: () => (
          <ACCommentEditor placeholder={'Custom Field'} showFooter={false} defaultFocus={true} users={[]} />
        ),
        rules: [editorRules({ maxByteSizeInKB: 100, maxMessage: true,  required: false })],
        NoDefaultLengthRule: true,
        valuePropName: "initialValue",
      };
  }
}